import { Row, Col, Button, Form, Modal as AntModal } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useForm } from "react-hook-form";
import axios from "axios";
import Constants from "../../Utils/Constants";
import useToken from "../../Authentication/useToken";
import PropTypes from "prop-types";
import CreateClinicModal from "../../Clinic/CreateClinicModal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ProfessionalIdentityFields from "../shared/ProfessionalIdentityFields";
import ClinicSelectorField from "../shared/ClinicSelectorField";

const clinicManagerFieldKeys = {
  labelFirstName: "Physicians.label_first_name",
  placeholderFirstName: "Physicians.placeholder_enter_first_name",
  errorRequiredFirstName: "Physicians.error_required_first_name",
  errorFirstNameMinLength: "Physicians.error_first_name_min_length",
  labelLastName: "Physicians.label_last_name",
  placeholderLastName: "Physicians.placeholder_enter_last_name",
  errorRequiredLastName: "Physicians.error_required_last_name",
  errorLastNameMinLength: "Physicians.error_last_name_min_length",
  labelEmail: "Physicians.label_email",
  placeholderEmail: "Physicians.placeholder_enter_email",
  errorRequiredEmail: "Physicians.error_required_email",
  errorInvalidEmailFormat: "Physicians.error_invalid_email_format",
  labelConfirmEmail: "Physicians.label_confirm_email",
  placeholderConfirmEmail: "Physicians.placeholder_confirm_email",
  errorRequiredEmailConfirmation: "Physicians.error_required_email_confirmation",
  errorEmailMismatch: "Physicians.error_email_mismatch",
  labelPhoneNumber: "Physicians.label_phone_number",
  placeholderPhoneNumber: "Physicians.placeholder_phone_number",
  errorRequiredPhoneNumber: "Physicians.error_required_phone_number",
  errorInvalidPhoneNumber: "Physicians.error_invalid_phone_number",
};

function CreateClinicManager({ refetchClinicManagers }) {
  const { t } = useTranslation("Professionals");
  const { token } = useToken();

  const [clinics, setClinics] = useState([]);
  const [clinicModalOpen, setClinicModalOpen] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const openModal = (msg, isError) => {
    AntModal[isError ? "error" : "success"]({
      content: <p>{msg}</p>,
      okText: t("Physicians.button_close"),
      centered: true,
      onOk: () => {
        if (!isError) reset();
      },
    });
  };

  const fetchClinics = async () => {
    try {
      const res = await axios.get(`${Constants.SERVER_URL}/clinics?active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClinics(res.data || []);
    } catch (e) {
      openModal(
        e?.response?.data?.message || t("Kinesiologist.error_loading_kinesiologists"),
        true
      );
    }
  };

  useEffect(() => {
    if (token) fetchClinics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = (data) => {
    const clinicManagerData = {
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      phoneNumber: data.phoneNumber,
      role: "clinic_manager",
      clinicId: data.clinicId,
    };

    axios
      .post(`${Constants.SERVER_URL}/create-professional-user`, clinicManagerData, {
        headers: { Authorization: "Bearer " + token },
      })
      .then(() => {
        refetchClinicManagers();
        openModal("Gestionnaire créé avec succès", false);
      })
      .catch((err) => {
        const backendMsg = err?.response?.data?.message;
        openModal(
          (backendMsg && t(`Backend:${backendMsg}`)) ||
            "Erreur lors de la création du gestionnaire",
          true
        );
      });
  };

  return (
    <Row justify="center">
      <Col span={16}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <ProfessionalIdentityFields
            t={t}
            control={control}
            errors={errors}
            getValues={getValues}
            keys={clinicManagerFieldKeys}
          />

          <Row gutter={16}>
            <Col span={12}>
              <ClinicSelectorField
                control={control}
                errors={errors}
                clinics={clinics}
                onOpenCreateClinic={() => setClinicModalOpen(true)}
              />

              <CreateClinicModal
                open={clinicModalOpen}
                onClose={() => setClinicModalOpen(false)}
                onCreated={(clinic) => {
                  fetchClinics();
                  if (clinic?.id) setValue("clinicId", clinic.id);
                }}
              />
            </Col>
          </Row>

          <Form.Item className="submit-button">
            <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
              Créer le gestionnaire
            </Button>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

CreateClinicManager.propTypes = {
  refetchClinicManagers: PropTypes.func.isRequired,
};

export default CreateClinicManager;
